/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { LicenseManager } from 'ag-grid-enterprise';
import { AuthCallback } from 'components/auth/AuthCallback/AuthCallback';
import AuthorizedRoute from 'components/auth/AuthorizedRoute/AuthorizedRoute';
import { RedirectSSO } from 'components/auth/RedirectSSO/RedirectSSO';
import { isNascarUser, isOnlyIndyUser } from 'components/auth/utils';
import { usePrimarySlugStatus } from 'components/dashboard/Workspace/WorkspaceUtils';
import { getDefaultStore } from 'jotai';
import {
  adminSettingsIndyRaceProgressionOriginAtom,
  adminSettingsNascarRaceProgressionOriginAtom,
} from 'jotai/adminPage/adminSettings';
import { AuthRoles, indyRoles, nascarRoles } from 'jotai/auth';
import { DashboardType } from 'jotai/dashboard';
import { SanctioningBodyEnum } from 'models/viewModels';
import { lazy, useEffect } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { authService } from 'services/auth/AuthService';
import { QtmThemeProvider } from 'theme';

// Lazy-loaded components
const AdminDashboard = lazy(() => import('./components/dashboard/AdminDashboard'));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const TrackTemperatureDashboard = lazy(() => import('./components/dashboard/TrackTemperatureDashboard'));
const EmptyDashboard = lazy(() => import('./components/dashboard/EmptyDashboard'));
const ImageGalleryDashboard = lazy(() => import('./components/dashboard/ImageGalleryDashboard'));
const IndyLiveDashboard = lazy(() => import('./components/dashboard/IndyLiveDashboard'));
const NascarLiveDashboard = lazy(() => import('./components/dashboard/NascarLiveDashboard'));
const MobileDashboard = lazy(() => import('./components/dashboard/MobileDashboard'));
const MobileLiveDashboard = lazy(() => import('./components/dashboard/MobileLiveDashboard'));

const UnauthenticatedAppRoutes = () => {
  return (
    <Routes>
      <Route path="/oauth2-callback" element={<AuthCallback />} />
      <Route path="*" element={<RedirectSSO />} />
    </Routes>
  );
};

const AuthenticatedAppRoutes = () => {
  localStorage.removeItem('next_url');
  const defaultStore = getDefaultStore();
  useEffect(() => {
    LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE);
    // Hydrates the admin race progression origins atoms to be used in the RSocketBaseService
    defaultStore.get(adminSettingsIndyRaceProgressionOriginAtom);
    defaultStore.get(adminSettingsNascarRaceProgressionOriginAtom);
  }, [defaultStore]);

  return (
    <Routes>
      <Route path="/" element={<EmptyDashboard type={DashboardType.Default} />} />
      <Route
        path="/mobile"
        element={
          <AuthorizedRoute authRoles={[...nascarRoles]} type={DashboardType.Default}>
            <MobileDashboard sanctioningBodyId={SanctioningBodyEnum.NASCAR} />
          </AuthorizedRoute>
        }
      />
      <Route
        path="/mobile/live"
        element={
          <AuthorizedRoute authRoles={[...nascarRoles]} type={DashboardType.Default}>
            <MobileLiveDashboard sanctioningBodyId={SanctioningBodyEnum.NASCAR} />
          </AuthorizedRoute>
        }
      />
      <Route path="/mobile/:sessionId?/" element={<MobileDashboard sanctioningBodyId={SanctioningBodyEnum.NASCAR} />} />
      <Route
        path="/live/:workspaceSlug?"
        element={
          <AuthorizedRoute authRoles={[...nascarRoles]} type={DashboardType.Default}>
            <NascarLiveDashboard />
          </AuthorizedRoute>
        }
      />
      <Route
        path="/tns"
        element={
          <AuthorizedRoute authRoles={[...nascarRoles]} type={DashboardType.Default}>
            <NascarLiveDashboard />
          </AuthorizedRoute>
        }
      />
      <Route path="/track-temperature" element={<TrackTemperatureDashboard />} />
      <Route
        path="/indy/:workspaceSlug?"
        element={
          <AuthorizedRoute authRoles={[...indyRoles]} type={DashboardType.Default}>
            <IndyLiveDashboard />
          </AuthorizedRoute>
        }
      />
      <Route
        path="/indy/mobile"
        element={
          <AuthorizedRoute authRoles={[...indyRoles]} type={DashboardType.Default}>
            <MobileDashboard sanctioningBodyId={SanctioningBodyEnum.INDYCAR} />
          </AuthorizedRoute>
        }
      />
      <Route
        path="/indy/mobile/live"
        element={
          <AuthorizedRoute authRoles={[...indyRoles]} type={DashboardType.Default}>
            <MobileLiveDashboard sanctioningBodyId={SanctioningBodyEnum.INDYCAR} />
          </AuthorizedRoute>
        }
      />

      <Route path="/image-gallery/:trackEventId" element={<ImageGalleryDashboard />} />
      <Route path="/image-gallery" element={<EmptyDashboard type={DashboardType.ImageGallery} />} />
      <Route path="/:sessionId/:workspaceSlug?" element={<Dashboard />} />
      <Route
        path="/admin/:path?"
        element={
          <AuthorizedRoute authRoles={[AuthRoles.ADMIN]} type={DashboardType.Default}>
            <AdminDashboard />
          </AuthorizedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AppRoutes = () => {
  const loggedIn = authService.isLoggedin();
  const navigate = useNavigate();
  const isCombinedView = usePrimarySlugStatus().isCombinedViewPrimarySlug;

  useEffect(() => {
    if (
      loggedIn &&
      isMobileOnly &&
      !authService.hasRole(process.env.REACT_APP_ROLE_COMPTOOLS_REALM.toString(), 'ignore-mobile') &&
      !/\/image-gallery.*/.test(window.location.pathname) &&
      !window.location.pathname.includes('mobile')
    ) {
      if (isOnlyIndyUser()) {
        navigate('indy/mobile');
      } else if (isNascarUser()) {
        navigate('mobile');
      }
      // Redirect to home page if user is logged in and tries to access the combined view
    } else if (loggedIn && isCombinedView) {
      navigate('/');
      // fixes issue where background doesn't update for user preferences
      navigate(0);
    }
  }, [loggedIn, navigate, isCombinedView]);

  /*  returns authenticated routes if user is logged in and is part of the authorized groups
      returns unauthenticated routes if the user is not logged in */

  return (
    <>
      {loggedIn ? (
        <QtmThemeProvider>
          <AuthenticatedAppRoutes />
        </QtmThemeProvider>
      ) : (
        <UnauthenticatedAppRoutes />
      )}
    </>
  );
};

export default AppRoutes;
